import React, { useState } from 'react';
import { Check, ArrowRight, Users, HeartPulse, Dumbbell, Music, ToggleRight, Gift, CalendarCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { useContent } from '../context/ContentContext';
import { useLang } from '../context/LanguageContext';
import PilotRequestModal from './PilotRequestModal';
import Reveal from './Reveal';

const ICONS = { care: HeartPulse, fit: Dumbbell, dance: Music };
const NOTE_ICONS = [ToggleRight, Gift, CalendarCheck];
import { PRODUCT_PATHS } from './ProductPage';

const ProductCard = ({ item, data, onPilotRequest }) => {
  const Icon = ICONS[item.id] || HeartPulse;
  const color = item.color || '#0071E3';
  const ctaLive = (data.cta_live || 'Zu {name}').replace('{name}', item.name);
  const { t } = useLang();

  return (
    <div
      id={`product-${item.id}`}
      className="bg-white rounded-3xl p-8 flex flex-col shadow-sm hover:shadow-xl transition-shadow duration-300 scroll-mt-28"
      data-testid={`product-card-${item.id}`}
    >
      {item.logo ? (
        <img
          src={item.logo}
          alt={`${item.name} Logo`}
          className="h-16 w-auto max-w-[150px] mb-5 object-contain"
          loading="lazy"
        />
      ) : (
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
          style={{ backgroundColor: `${color}1A` }}
        >
          <Icon className="w-7 h-7" style={{ color }} strokeWidth={1.8} />
        </div>
      )}

      <h3 className="text-2xl font-semibold text-[#1D1D1F] mb-2">{item.name}</h3>
      <p className="text-[#424245] font-medium mb-3">{item.claim}</p>

      <div className="flex items-start gap-2 mb-5">
        <Users className="w-4 h-4 text-[#86868B] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
        <p className="text-sm text-[#86868B]">{item.audience}</p>
      </div>

      <ul className="space-y-2.5 mb-8 flex-1">
        {(item.features || []).map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-[#424245]">
            <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color }} strokeWidth={2.5} />
            <span>
              {f.text}
              {f.coming_soon && (
                <span
                  className="ml-2 inline-block align-middle text-[10px] font-semibold uppercase tracking-wide bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full"
                  data-testid={`feature-soon-badge-${item.id}-${i}`}
                >
                  {data.badge_soon || 'Bald verfügbar'}
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>

      {item.price_from && (
        <p className="text-sm font-semibold mb-3 text-center" style={{ color }} data-testid={`product-price-${item.id}`}>
          {item.price_from}
        </p>
      )}

      {item.subdomain_live ? (
        <a href={item.url} target="_blank" rel="noopener noreferrer" className="block">
          <Button
            className="w-full rounded-full h-12 text-white hover:opacity-90 transition-opacity group"
            style={{ backgroundColor: color }}
            data-testid={`product-cta-${item.id}`}
          >
            {ctaLive}
            <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Button>
        </a>
      ) : (
        <Button
          variant="outline"
          onClick={() => onPilotRequest(item.id)}
          className="w-full rounded-full h-12 bg-transparent hover:bg-transparent hover:opacity-70 transition-opacity"
          style={{ borderColor: color, color }}
          data-testid={`product-cta-${item.id}`}
        >
          {data.cta_soon || 'Bald verfügbar – Pilotkunde werden'}
        </Button>
      )}

      <Link
        to={PRODUCT_PATHS[item.id] || '/'}
        className="text-sm text-center mt-4 hover:underline font-medium"
        style={{ color }}
        data-testid={`product-more-link-${item.id}`}
      >
        {t('projects.more')} →
      </Link>
    </div>
  );
};

const ProductsSection = () => {
  const data = useContent('products', null);
  const [pilotProduct, setPilotProduct] = useState(null);

  if (!data || !(data.items || []).length) return null;
  const note = data.platform_note || {};

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: data.items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'SoftwareApplication',
        name: item.name,
        description: `${item.claim}. ${item.audience}`,
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        url: item.subdomain_live ? item.url : `https://www.invictaflow.ch${PRODUCT_PATHS[item.id] || ''}`,
        publisher: { '@type': 'Organization', name: 'InvictaX Group GmbH', url: 'https://www.invictaflow.ch' },
      },
    })),
  };

  return (
    <section id="solutions" className="py-24 px-6 bg-[#F5F5F7]" data-testid="products-section">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <div className="max-w-[1080px] mx-auto">
        <Reveal className="text-center mb-16">
          <p className="text-xs font-semibold text-[#0071E3] tracking-widest uppercase mb-4">
            {data.eyebrow}
          </p>
          <h2 className="text-4xl md:text-5xl font-semibold text-[#1D1D1F] mb-4 tracking-tight">
            {data.section_title}
          </h2>
          <p className="text-base md:text-lg text-[#86868B] max-w-2xl mx-auto">
            {data.section_subtitle}
          </p>
        </Reveal>

        <Reveal delay={100}>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {data.items.map((item) => (
              <ProductCard key={item.id} item={item} data={data} onPilotRequest={setPilotProduct} />
            ))}
          </div>
        </Reveal>

        {note.title && (
          <Reveal delay={150}>
            <div className="bg-[#1D1D1F] rounded-3xl p-8 md:p-10 text-center" data-testid="platform-note">
              <h3 className="text-xl md:text-2xl font-semibold text-white mb-3">{note.title}</h3>
              <p className="text-[#A1A1A6] max-w-2xl mx-auto mb-6">{note.text}</p>
              <div className="flex flex-wrap justify-center gap-x-8 gap-y-3">
                {(note.points || []).map((point, i) => {
                  const NoteIcon = NOTE_ICONS[i % NOTE_ICONS.length];
                  return (
                    <div key={i} className="flex items-center gap-2 text-sm text-white">
                      <NoteIcon className="w-4 h-4 text-[#2997FF]" strokeWidth={1.8} />
                      <span>{point}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </Reveal>
        )}
      </div>

      {pilotProduct && (
        <PilotRequestModal
          products={data.items}
          preselect={pilotProduct}
          onClose={() => setPilotProduct(null)}
        />
      )}
    </section>
  );
};

export default ProductsSection;
